import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  TextField,
  MenuItem,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  TablePagination
} from '@mui/material';
import { CalendarToday, AddComment, Edit, Delete, Add, Settings } from '@mui/icons-material';
import { useQuery, useMutation } from '@apollo/client/react';
import { useNavigate } from 'react-router-dom';
import { TASKS_LIST_QUERY, TYPE_OF_TASKS_QUERY, PEOPLE_QUERY, CREATE_OR_UPDATE_TASK } from '../../graphql/tasks';
import { BOARDS_LIST_QUERY } from '../../graphql/boards';
import { ProjectsListQuery, PROJECT_TREE_QUERY, GET_COMPANIES } from '../../graphql/projects';
import LoadingOverlay from '../common/LoadingOverlay';
import TaskViewModal from '../tasks/TaskViewModal';
import TaskFormModal from '../tasks/TaskFormModal';
import TaskProgressModal from '../tasks/TaskProgressModal';
import TasksFilters from '../tasks/TasksFilters';
import BoardFormModal from '../boards/BoardFormModal';

const Planning = () => {
  const navigate = useNavigate();
  const [draggedTask, setDraggedTask] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskViewModalOpen, setTaskViewModalOpen] = useState(false);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [taskProgressModalOpen, setTaskProgressModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);

  const params = new URLSearchParams(window.location.search);
  const [page, setPage] = useState(parseInt(params.get('page')) || 0);
  const [rowsPerPage, setRowsPerPage] = useState(parseInt(params.get('rowsPerPage')) || 25);

  // Initialize filters from URL
  const getFiltersFromURL = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      name: params.get('name') || '',
      project: params.get('project') || '',
      element: params.get('element') || '',
      company: params.get('company') || '',
      types: params.get('types') || '',
      priority: params.get('priority') || '',
      complete: params.get('complete') || '',
      responsible: params.get('responsible') || '',
      dateFrom: params.get('dateFrom') || '',
      dateTo: params.get('dateTo') || ''
    };
  };

  const [filters, setFilters] = useState(getFiltersFromURL);
  const [currentURL, setCurrentURL] = useState(window.location.search);

  const { data: tasksData, loading: tasksLoading, refetch: refetchTasks } = useQuery(TASKS_LIST_QUERY, {
    variables: {
      page: page + 1,
      size: rowsPerPage,
      sort_column: 'id',
      sort_direction: 'ASC',
      filters: {
        board_column_id: null,
        project_id: filters.project ? parseInt(filters.project) : null,
        element_id: filters.element ? parseInt(filters.element) : null,
        company_ids: filters.company ? [parseInt(filters.company)] : null,
        priority: filters.priority ? parseInt(filters.priority) : null,
        name: filters.name || null,
        task_types: filters.types ? [filters.types.toString()] : null,
        responsible_ids: filters.responsible ? [parseInt(filters.responsible)] : null,
        complete: filters.complete ? (
          filters.complete === 'New' ? 0 :
            filters.complete === 'Done' ? 100 :
              filters.complete === 'In progress' ? -1 : null
        ) : null,
        start_date_from: filters.dateFrom ? new Date(filters.dateFrom + 'T00:00:00.000Z').toISOString() : null,
        start_date_to: filters.dateTo ? new Date(filters.dateTo + 'T23:59:59.999Z').toISOString() : null
      }
    },
    fetchPolicy: 'cache-and-network'
  });

  const { data: boardsData, loading: boardsLoading, refetch: refetchBoards } = useQuery(BOARDS_LIST_QUERY, {
    fetchPolicy: 'cache-first'
  });

  const { data: typeOfTasksData } = useQuery(TYPE_OF_TASKS_QUERY, {
    fetchPolicy: 'cache-first'
  });

  const { data: peopleData } = useQuery(PEOPLE_QUERY, {
    variables: { page: 1, size: 10000 },
    fetchPolicy: 'cache-first'
  });

  const { data: projectsData } = useQuery(ProjectsListQuery, {
    variables: { pageSize: 1000, page: 1 },
    fetchPolicy: 'cache-first'
  });

  const { data: companiesData } = useQuery(GET_COMPANIES, {
    fetchPolicy: 'cache-first'
  });

  const { data: allProjectsTreeData } = useQuery(PROJECT_TREE_QUERY, {
    variables: { projectId: 1 },
    fetchPolicy: 'cache-first'
  });

  // Get project tree for selected filter project
  const { data: projectTreeData } = useQuery(PROJECT_TREE_QUERY, {
    variables: { projectId: filters.project },
    fetchPolicy: 'cache-first',
    skip: !filters.project
  });

  // Flatten project tree into lookup for elements
  const allElements = useMemo(() => {
    const elements = {};
    if (projectTreeData?.project_tree) {
      try {
        const parsed = JSON.parse(projectTreeData.project_tree);
        const flattenTree = (items, depth = 0) => {
          items.forEach(item => {
            elements[item.id] = { ...item, depth };
            if (item.children) flattenTree(item.children, depth + 1);
          });
        };
        const itemsArray = Array.isArray(parsed) ? parsed : [parsed];
        flattenTree(itemsArray);
      } catch (error) {
        console.error('Error parsing project tree:', error);
      }
    }
    return elements;
  }, [projectTreeData]);

  const [createOrUpdateTask] = useMutation(CREATE_OR_UPDATE_TASK);
  const [boardModalOpen, setBoardModalOpen] = useState(false);

  const allProjects = useMemo(() => {
    const projects = {};
    if (allProjectsTreeData?.project_tree) {
      try {
        const parsed = JSON.parse(allProjectsTreeData.project_tree);
        const flattenTree = (items) => {
          items.forEach(item => {
            projects[item.id] = item;
            if (item.children) flattenTree(item.children);
          });
        };
        const itemsArray = Array.isArray(parsed) ? parsed : [parsed];
        flattenTree(itemsArray);
      } catch (error) {
        console.error('Error parsing projects tree:', error);
      }
    }
    return projects;
  }, [allProjectsTreeData]);

  const handleDragStart = (task) => {
    setDraggedTask(task);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDropToBoard = async (e, board) => {
    e.preventDefault();
    if (!draggedTask) return;

    const defaultColumn = board.board_columns?.find(col => col.is_default);
    if (!defaultColumn) return;

    try {
      await createOrUpdateTask({
        variables: {
          id: parseInt(draggedTask.id),
          board_column_id: parseInt(defaultColumn.id)
        }
      });
      setDraggedTask(null);
      refetchTasks();
    } catch (error) {
      console.error('Error assigning task to board:', error);
    }
  };

  // Update state when URL changes
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setPage(parseInt(params.get('page')) || 0);
    setRowsPerPage(parseInt(params.get('rowsPerPage')) || 25);
  }, [window.location.search]);

  // Reset page and refetch when filters change
  useEffect(() => {
    if (window.location.search !== currentURL) {
      setPage(0);
      setCurrentURL(window.location.search);
      refetchTasks();
    }
  }, [filters, currentURL, refetchTasks]);

  if (tasksLoading || boardsLoading) return <LoadingOverlay />;

  const tasks = tasksData?.tasks?.list?.filter(task => task !== null) || [];
  const boards = boardsData?.boards || [];
  const metadata = tasksData?.tasks?.metadata || {};

  const handleChangePage = (event, newPage) => {
    const params = new URLSearchParams(window.location.search);
    params.set('page', newPage.toString());
    navigate(`/planning?${params.toString()}`);
  };

  const handleChangeRowsPerPage = (event) => {
    const params = new URLSearchParams(window.location.search);
    params.set('rowsPerPage', event.target.value);
    params.set('page', '0');
    navigate(`/planning?${params.toString()}`);
  };
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <CalendarToday sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" component="h1">
          Planning
        </Typography>
      </Box>

      <TasksFilters
        filters={filters}
        setFilters={setFilters}
        projectsData={projectsData}
        allElements={allElements}
        typeOfTasksData={typeOfTasksData}
        peopleData={peopleData}
        companiesData={companiesData}
      />

      <Grid container spacing={3}>
        {/* Unassigned Tasks */}
        <Grid item xs={12} md={9}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Unassigned Tasks ({tasks.length})
            </Typography>
            <TableContainer
              sx={{
                height: 'auto',
                maxHeight: '70vh',
                overflow: 'auto'
              }}>
              <Table
                stickyHeader
                size="small"
                sx={{
                  tableLayout: 'fixed',
                  width: '100%'
                }}
              >
                {/* HEADER */}
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '16%' }}>
                      Name
                    </TableCell>
                    <TableCell sx={{ width: '14%' }}>
                      Project / Element
                    </TableCell>
                    <TableCell sx={{ width: '12%' }}>
                      Company
                    </TableCell>
                    <TableCell sx={{ width: '12%' }}>
                      Responsible
                    </TableCell>
                    <TableCell sx={{ width: '12%' }}>
                      Types
                    </TableCell>
                    <TableCell sx={{ width: '8%' }}>
                      Priority
                    </TableCell>
                    <TableCell sx={{ width: '10%' }}>
                      Dates
                    </TableCell>
                    <TableCell align="center" sx={{ width: '6%' }}>
                      <IconButton
                        size="small"
                        disabled
                        sx={{
                          opacity: 1
                        }}
                      >
                        <Settings fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                </TableHead>
                {/* BODY */}
                <TableBody>
                  {tasks.map((task) => (
                    <TableRow
                      key={task.id}
                      hover
                      draggable
                      onDragStart={() => handleDragStart(task)}
                      sx={{
                        cursor: 'grab',
                        '&:active': { cursor: 'grabbing' },
                        '&:nth-of-type(odd)': { backgroundColor: 'action.hover' }
                      }}
                    >
                      {/* NAME */}
                      <TableCell>
                        <Tooltip title={task.name || ''} arrow>
                          <Typography noWrap>
                            {task.name}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      {/* PROJECT */}
                      <TableCell>
                        <Tooltip title={task.project?.name || ''} arrow>
                          <Typography noWrap>
                            {task.project?.name || '-'}
                          </Typography>
                        </Tooltip>
                        <Tooltip title={task.element?.name || ''} arrow>
                          <Typography
                            noWrap
                            variant="caption"
                            color="text.secondary"
                          >
                            {task.element?.name || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      {/* COMPANY */}
                      <TableCell>
                        <Tooltip title={task.companies?.[0]?.name || ''} arrow>
                          <Typography noWrap>
                            {task.companies?.[0]?.name || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      {/* RESPONSIBLE */}
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 0.5,
                            flexWrap: 'nowrap',
                            overflow: 'hidden'
                          }}
                        >
                          {task.responsible?.map((person, index) => (
                            <Tooltip
                              key={index}
                              title={`${person.last_name} ${person.first_name}`}
                              arrow
                            >
                              <Chip
                                label={person.last_name}
                                size="small"
                              />
                            </Tooltip>
                          ))}
                        </Box>
                      </TableCell>
                      {/* TYPES */}
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 0.5,
                            flexWrap: 'nowrap',
                            overflow: 'hidden'
                          }}
                        >
                          {task.task_type?.map((type, index) => (
                            <Tooltip key={index} title={type.name} arrow>
                              <Chip
                                label={type.name}
                                size="small"
                                variant="outlined"
                              />
                            </Tooltip>
                          ))}
                        </Box>
                      </TableCell>
                      {/* PRIORITY */}
                      <TableCell>
                        <Chip
                          label={
                            ['Highest', 'High', 'Normal', 'Low', 'Lowest'][task.priority]
                            || 'Normal'
                          }
                          color={
                            ['error', 'warning', 'info', 'success', 'default'][task.priority]
                            || 'info'
                          }
                          size="small"
                        />
                      </TableCell>
                      {/* DATES */}
                      <TableCell>

                        <Tooltip
                          title={
                            task.start_date
                              ? new Date(task.start_date).toLocaleDateString('pl-PL')
                              : ''
                          }
                        >
                          <Typography wrap variant="caption">
                            from {
                              task.start_date
                                ? new Date(task.start_date).toLocaleDateString('pl-PL')
                                : '-'
                            }
                          </Typography>
                        </Tooltip>
                        {task.end_date && (
                          <Tooltip
                            title={new Date(task.end_date).toLocaleDateString('pl-PL')}
                          >
                            <Typography wrap>
                              to {new Date(task.end_date).toLocaleDateString('pl-PL')}
                            </Typography>
                          </Tooltip>

                        )}

                      </TableCell>
                      {/* ACTIONS */}
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => {
                            setSelectedTask(task);
                            setTaskProgressModalOpen(true);
                          }}
                        >
                          <AddComment fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            setSelectedTask(task);
                            setTaskModalOpen(true);
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setTaskToDelete(task);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={metadata.total_count || 0}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Tasks per page:"
            />
          </Paper>

        </Grid>
        {/* Boards */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                Boards ({boards.length})
              </Typography>
              <IconButton
                size="small"
                color="primary"
                onClick={() => setBoardModalOpen(true)}
              >
                <Add />
              </IconButton>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5
              }}
            >
              {boards.map((board, index) => {
                const lightness = 92 - (index * 8);
                return (
                  <Tooltip
                    key={board.id}
                    title={board.name}
                    arrow
                    placement="left"
                  >
                    <Card
                      onDragOver={handleDragOver}
                      onDrop={(e) =>
                        handleDropToBoard(e, board)
                      }
                      sx={{
                        width: '100%',
                        minHeight: 80,
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        transition: '0.2s',
                        border: '1px solid',
                        borderColor: 'divider',
                        '&:hover': {
                          transform: 'scale(1.02)',
                          boxShadow: 3
                        }
                      }}
                    >
                      <CardContent
                        sx={{
                          width: '100%',
                          '&:last-child': {
                            pb: 2
                          }
                        }}
                      >
                        <Typography
                          fontWeight="bold"
                          noWrap
                        >
                          {board.name}
                        </Typography>
                        {(board.start_date || board.end_date) && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            {board.start_date &&
                              new Date(board.start_date).toLocaleDateString()
                            }
                            {board.start_date &&
                              board.end_date &&
                              ' — '
                            }
                            {board.end_date &&
                              new Date(board.end_date).toLocaleDateString() + ' '
                            }
                          </Typography>
                        )}
                        <Box>
                          <Typography
                            variant="caption"
                            wrap
                            sx={{
                              opacity: 0.6
                            }}
                          >
                            Drop task here
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Tooltip>
                );

              })}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <TaskViewModal
        open={taskViewModalOpen}
        onClose={() => {
          setTaskViewModalOpen(false);
          setSelectedTask(null);
        }}
        taskId={selectedTask?.id}
      />

      <TaskFormModal
        open={taskModalOpen}
        onClose={() => {
          setTaskModalOpen(false);
          setSelectedTask(null);
        }}
        taskId={selectedTask?.id || null}
        onSave={async () => {
          await refetchTasks();
        }}
      />

      <TaskProgressModal
        open={taskProgressModalOpen}
        onClose={() => {
          setTaskProgressModalOpen(false);
          setSelectedTask(null);
        }}
        taskId={selectedTask?.id}
        onSave={async () => {
          await refetchTasks();
        }}
      />

      <BoardFormModal
        open={boardModalOpen}
        onClose={() => setBoardModalOpen(false)}
        onSave={async () => {
          await refetchBoards();
        }}
      />
    </Box>
  );
};

export default Planning;
